import {observable, action} from 'mobx';
import {message} from '@enos/dpl';
import localeText from '@locale';
import axiosInstance from '~/utils/axiosInstance';
import uris from '~/uris/index';
import Asset from './models/Asset';

class AssetPermissionStore {
  @observable userAssignedAssets = [];

  @observable userGroupAssignedAssets = [];

  @observable userAssets = [];

  @observable userGroupAssets = [];

  @observable isLoading = false;

  @observable currentUser = {};

  @observable currentUserGroup = {};

  @observable initUserAssetTargetKeys = [];

  @observable userAssetTargetKeys = [];

  @observable inituserGroupAssetTargetKeys = [];

  @observable userGroupAssetTargetKeys = [];

  @observable selectedKeys = [];

  @observable activeTab = '1';

  @action.bound
  setActiveTab(key) {
    this.activeTab = key;
  }

  @action resetLoading() {
    this.isLoading = false;
  }

  @action.bound
  setUserAssignedAssets(data) {
    this.userAssignedAssets = data;
  }

  @action.bound
  setUserGroupAssignedAssets(data) {
    this.userGroupAssignedAssets = data;
  }

  @action.bound
  setUserAssets(data) {
    this.userAssets = data;
  }

  @action.bound
  setUserGroupAssets(data) {
    this.userGroupAssets = data;
  }

  @action.bound
  getUserAssetAssignList(userId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getUserAssetAssignList(), null, {
        params: {userId}
      })
      .then((data) => {
        this.setUserAssignedAssets(data.assets.map(json => this.updateUserAssignedAssetFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.setUserAssignedAssets([]);
        this.resetLoading();
      });
  }

  @action.bound
  updateUserAssignedAssetFromServer(json) {
    let userAssignedAsset = this.userAssignedAssets.find(({id}) => id === json.id);
    if (!userAssignedAsset) {
      userAssignedAsset = new Asset(this, json.id);
    }
    userAssignedAsset.updateFromJson(json);
    return userAssignedAsset;
  }

  @action.bound
  setCurrentUser(data) {
    this.currentUser = data;
  }

  @action.bound
  setCurrentUserGroup(data) {
    this.currentUserGroup = data;
  }

  @action.bound
  async getUserAssetList(userId) {
    this.isLoading = true;
    await axiosInstance
      .post(uris.getUserAssetList(), null, {
        params: {userId}
      })
      .then((data) => {
        const keys = [];
        data.assets.forEach((a) => {
          if (a.isAssigned) {
            keys.push(a.id);
          }
        });
        this.setUserTargetKeys(keys);
        this.setInitUserTargetKeys(keys);
        this.setUserAssets(data.assets.map(json => this.updateUserAssetFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateUserAssetFromServer(json) {
    let userAsset = this.userAssets.find(({id}) => id === json.id);
    if (!userAsset) {
      userAsset = new Asset(this, json.id);
    }
    userAsset.updateFromJson(json);
    return userAsset;
  }

  @action.bound
  setUserTargetKeys(keys) {
    this.userAssetTargetKeys = keys;
  }

  @action.bound
  setInitUserTargetKeys(keys) {
    this.initUserAssetTargetKeys = keys;
  }

  @action.bound
  setUserGroupTargetKeys(keys) {
    this.userGroupAssetTargetKeys = keys;
  }

  @action.bound
  setInitUserGroupTargetKeys(keys) {
    this.inituserGroupAssetTargetKeys = keys;
  }

  @action.bound
  assignAssetForUser() {
    this.isLoading = true;
    axiosInstance
      .post(uris.assignAssetForUser(), {
        userId: this.currentUser.id,
        assetIds: this.userAssetTargetKeys
      })
      .then(() => {
        this.getUserAssetAssignList(this.currentUser.id);
        this.resetLoading();
        message.success(localeText('ASSET_TREE.MSG_PROMPT_ASSETS_ALLOCATION'));
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  removeAssetFromUser(assetId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.removeAssetFromUser(), {
        userId: this.currentUser.id,
        assetId
      })
      .then(() => {
        this.resetLoading();
        this.getUserAssetAssignList(this.currentUser.id);
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  getUserGroupAssetAssignList(userGroupId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getUserGroupAssetAssignList(), null, {params: {userGroupId}})
      .then((data) => {
        this.setUserGroupAssignedAssets(data.assets.map(json => this.updateUserGroupAssignedAssetFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateUserGroupAssignedAssetFromServer(json) {
    let userGroupAssignedAsset = this.userGroupAssignedAssets.find(({id}) => id === json.id);
    if (!userGroupAssignedAsset) {
      userGroupAssignedAsset = new Asset(this, json.id);
    }
    userGroupAssignedAsset.updateFromJson(json);
    return userGroupAssignedAsset;
  }

  @action.bound
  getUserGroupAssetList(userGroupId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.getUserGroupAssetList(), null, {params: {userGroupId}})
      .then((data) => {
        const keys = [];
        data.assets.forEach((a) => {
          if (a.isAssigned) {
            keys.push(a.id);
          }
        });
        this.setUserGroupTargetKeys(keys);
        this.setInitUserGroupTargetKeys(keys);
        this.setUserGroupAssets(data.assets.map(json => this.updateUserGroupAssetFromServer(json)));
        this.resetLoading();
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  updateUserGroupAssetFromServer(json) {
    let userGroupAsset = this.userGroupAssets.find(({id}) => id === json.id);
    if (!userGroupAsset) {
      userGroupAsset = new Asset(this, json.id);
    }
    userGroupAsset.updateFromJson(json);
    return userGroupAsset;
  }

  @action.bound
  assignAssetForUserGroup() {
    this.isLoading = true;
    axiosInstance
      .post(uris.assignAssetForUserGroup(), {
        userGroupId: this.currentUserGroup.id,
        assetIds: this.userGroupAssetTargetKeys
      })
      .then(() => {
        this.getUserGroupAssetAssignList(this.currentUserGroup.id);
        this.resetLoading();
        message.success(localeText('ASSET_TREE.MSG_PROMPT_ASSETS_ALLOCATION'));
      })
      .catch(() => {
        this.resetLoading();
      });
  }

  @action.bound
  removeAssetFromUserGroup(assetId) {
    this.isLoading = true;
    axiosInstance
      .post(uris.removeAssetFromUserGroup(), {
        userGroupId: this.currentUserGroup.id,
        assetId
      })
      .then(() => {
        this.resetLoading();
        this.getUserGroupAssetAssignList(this.currentUserGroup.id);
      })
      .catch(() => {
        this.resetLoading();
      });
  }
}

export default new AssetPermissionStore();
